/**
 * Static component registry with auto-discovery at module load time.
 *
 * This module provides a single source of truth for all dashboard components,
 * discovered at module load time via import.meta.glob. This eliminates timing
 * issues from hook-based discovery and enables registry-derived schemas.
 *
 * Key features:
 * - Discovery happens at module load, not during React render
 * - Registry is static and available synchronously
 * - New components auto-register via glob pattern matching
 *
 * @example
 * // Get a component by type
 * const entry = getComponent('chart');
 * if (entry) {
 *   const Chart = entry.component;
 *   return <Chart {...props} />;
 * }
 */
import type { ComponentType } from 'react';
import type { z } from 'zod';

/**
 * Shape of a discovered dashboard component in the registry.
 *
 * @property type - Component type discriminator (e.g., 'chart', 'kpi')
 * @property schema - Co-located Zod schema with type literal discriminator
 * @property component - React component function
 */
export interface DashboardComponentEntry {
  type: string;
  schema: z.ZodObject<{ type: z.ZodLiteral<string> } & z.ZodRawShape>;
  component: ComponentType<unknown>;
}

/**
 * Type guard for modules that export a schema component.
 * Schema components have a $$schema property attached by createGridComponent().
 */
interface SchemaComponent {
  $$schema?: z.ZodObject<z.ZodRawShape>;
}

function isSchemaComponent(value: unknown): value is SchemaComponent & ((...args: unknown[]) => unknown) {
  return typeof value === 'function' && '$$schema' in value;
}

function isZodLiteralTypeField(value: unknown): value is z.ZodLiteral<string> {
  if (typeof value !== 'object' || value === null || !('_def' in value)) {
    return false;
  }
  const def = (value as z.ZodType)._def as { type?: string };
  return def.type === 'literal';
}

function hasTypeLiteralDiscriminator(
  schema: z.ZodObject<z.ZodRawShape>,
): schema is z.ZodObject<{ type: z.ZodLiteral<string> } & z.ZodRawShape> {
  if (!('shape' in schema) || !schema.shape || typeof schema.shape !== 'object') {
    return false;
  }
  const shape = schema.shape as Record<string, unknown>;
  if (!('type' in shape)) {
    return false;
  }
  return isZodLiteralTypeField(shape.type);
}

function getLiteralValue(literalField: z.ZodLiteral<string>): string {
  const def = literalField._def as { values: string[] };
  return def.values[0] ?? '';
}

function discoverComponents(modules: Record<string, Record<string, unknown>>): DashboardComponentEntry[] {
  const entries: DashboardComponentEntry[] = [];
  const seenTypes = new Set<string>();

  for (const [path, module] of Object.entries(modules)) {
    const componentName = path.match(/.*\/([^/]+)\.component\.tsx$/)?.[1];
    if (!componentName) {
      continue;
    }

    const component = module[componentName];
    if (!isSchemaComponent(component)) {
      if (import.meta.env.DEV) {
        console.warn(`[Registry] Component "${componentName}" at ${path} missing $$schema marker`);
      }
      continue;
    }

    const schema = component.$$schema;
    if (!schema || !hasTypeLiteralDiscriminator(schema)) {
      if (import.meta.env.DEV) {
        console.warn(`[Registry] Component "${componentName}" at ${path} has invalid or missing type discriminator`);
      }
      continue;
    }

    const typeValue = getLiteralValue(schema.shape.type as z.ZodLiteral<string>);

    if (seenTypes.has(typeValue)) {
      if (import.meta.env.DEV) {
        console.warn(`[Registry] Duplicate type "${typeValue}" at ${path}, using first occurrence`);
      }
      continue;
    }
    seenTypes.add(typeValue);

    entries.push(
      Object.freeze({
        type: typeValue,
        schema,
        component: component as ComponentType<unknown>,
      }),
    );
  }

  return entries;
}

/**
 * Eager glob import of all dashboard panel components.
 * Executed at module load time, not in hooks.
 *
 * Two patterns are used for discovery:
 * 1. Relative path: Works during Vite dev/build (package context)
 * 2. Absolute path: Works during Vitest tests (root context)
 *
 * Components must use `*.component.tsx` naming convention to be discovered.
 */
const modules = import.meta.glob<Record<string, unknown>>(
  ['./panels/*/*.component.tsx', '/src/components/dashboard/panels/*/*.component.tsx'],
  { eager: true },
);

/**
 * Static registry built at module load time.
 * Contains all discovered dashboard components with their schemas.
 *
 * @remarks
 * The registry is frozen (immutable) to prevent accidental modification.
 * Discovery happens once at module load, not on each access.
 */
export const dashboardRegistry: readonly DashboardComponentEntry[] = Object.freeze(discoverComponents(modules));

/**
 * Look up a component entry by its type discriminator.
 *
 * @param type - The component type to look up (e.g., 'chart', 'table', 'kpi')
 * @returns The component entry if found, undefined otherwise
 *
 * @example
 * getComponent('chart')
 * getComponent('kpi')
 */
export function getComponent(type: string): DashboardComponentEntry | undefined {
  return dashboardRegistry.find((c) => c.type === type);
}

/**
 * Get the skeleton component for a registered component type.
 *
 * Components define their skeleton via the third argument to createGridComponent().
 * This function extracts the .Skeleton property from the component.
 *
 * @param type - The component type to look up
 * @returns The skeleton component if defined, undefined otherwise
 *
 * @example
 * const ChartSkeleton = getComponentSkeleton('chart');
 * if (ChartSkeleton) {
 *   return <ChartSkeleton />;
 * }
 */
export function getComponentSkeleton(type: string): ComponentType<unknown> | undefined {
  const entry = getComponent(type);
  if (!entry) {
    return undefined;
  }
  // The component may have a .Skeleton property attached by createGridComponent()
  // biome-ignore lint/style/useNamingConvention: Skeleton follows React conventions for static component properties
  const componentWithSkeleton = entry.component as { Skeleton?: ComponentType<unknown> };
  return componentWithSkeleton.Skeleton;
}
